<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import type { MonthlyUnPaidArrival } from '../domains/entities/MonthlyData';
import { ArrivalStatus } from '@/coreDomainsModule/entities/arrival';
import { i18n } from '@/i18n';

/**
 * propsの定義
 * @param arrivalStatus - 注文ステータス
 * @param unit - 単位
 * @param unPaidArrivals - 未払いの注文
 */
type Props = {
  arrivalStatus: ArrivalStatus;
  unit?: string;
  unPaidArrivals?: MonthlyUnPaidArrival[];
  approvalRequestedArrival?: MonthlyUnPaidArrival[];
};

const t = i18n.t;

const props = withDefaults(defineProps<Props>(), { unit: '', unPaidArrivals: undefined, approvalRequestedArrival: undefined });

const unit = computed(() => props.unit ?? '');

const textLines = computed(() => {
  switch (props.arrivalStatus) {
    case ArrivalStatus.Unpaid: {
      const resultStr: string[] = [];
      props.unPaidArrivals?.forEach((arrival) => {
        resultStr.push(
          t('納期情報バッヂ', {
            day: arrival.crossDate?.day,
            month: t(`${arrival.crossDate?.month}月`),
            value: `${arrival.amount.addComma()}${unit.value}`,
          })
        );
      });
      return resultStr;
    }
    case ArrivalStatus.ApprovalRequested: {
      const resultStr: string[] = [];
      props.approvalRequestedArrival?.forEach((arrival) => {
        resultStr.push(
          t('納期情報バッヂ', {
            day: arrival.crossDate?.day,
            month: t(`${arrival.crossDate?.month}月`),
            value: `${arrival.amount.addComma()}${unit.value}`,
          })
        );
      });
      return resultStr;
    }
    default:
      return [];
  }
});

const tooltipVisible = ref(false);
const tooltipPosition = ref({ top: 0, left: 0 });
let targetElement: HTMLElement | null = null;

/**
 * ツールチップの表示を切り替える
 * @param event - マウスイベント
 */
const showTooltip = (event: MouseEvent) => {
  tooltipVisible.value = true;

  // 親要素の位置を取得してツールチップの位置を計算
  targetElement = event.currentTarget as HTMLElement;
  const rect = targetElement.getBoundingClientRect();

  tooltipPosition.value = {
    top: rect.top + window.scrollY - tooltipHeight.value - 2.5, // 親要素の上に表示
    left: rect.left + window.scrollX + rect.width / 2, // 親要素の中央に配置
  };
};

const tooltipHeight = computed(() => {
  return 10 + 20 * textLines.value.length;
});

const tooltipHeightPixel = computed(() => {
  return `${tooltipHeight.value}px`;
});

/**
 * ツールチップを非表示にする
 */
const hideTooltip = () => {
  tooltipVisible.value = false;
  targetElement = null;
};

/**
 * スクロール時の非表示処理
 */
let scrollTimeout: number | null = null;

/**
 * スクロール時の非表示処理（50ms後に実行）
 */
const handleScroll = () => {
  if (scrollTimeout !== null) {
    clearTimeout(scrollTimeout);
  }

  scrollTimeout = window.setTimeout(() => {
    hideTooltip();
  }, 50); // スクロール終了後に非表示にするため50ms待機
};

onMounted(() => {
  // スクロールが発生したらツールチップを非表示にする
  window.addEventListener('scroll', handleScroll, true); // true を指定してキャプチャフェーズでイベントをキャッチ
  document.addEventListener('scroll', handleScroll, true); // ドキュメント全体も監視
});

onBeforeUnmount(() => {
  // コンポーネントが破棄される前にイベントリスナーを削除
  window.removeEventListener('scroll', handleScroll, true);
  document.removeEventListener('scroll', handleScroll, true);
});
</script>

<template>
  <div class="notification-badge__container" @mouseenter="showTooltip">
    <div :class="`notification-badge__circle notification-badge__circle--${arrivalStatus.toLowerCase()}`" />
    <!-- Tooltip -->
    <Teleport to="body">
      <div
        v-if="[ArrivalStatus.Unpaid, ArrivalStatus.ApprovalRequested].includes(arrivalStatus) && tooltipVisible"
        class="notification-badge__tooltip"
        :style="{ top: tooltipPosition.top + 'px', left: tooltipPosition.left + 'px', transform: 'translateX(-50%)' }"
        @mouseleave="hideTooltip"
      >
        <span v-for="(line, index) in textLines" :key="index">{{ line }}<br /></span>
      </div>
    </Teleport>
  </div>
</template>

<style lang="scss" scoped>
.notification-badge {
  &__container {
    position: relative;
    display: inline-block;
  }

  &__circle {
    width: 5px;
    height: 5px;
    border-radius: 50%;

    &--unpaid {
      background-color: $surfaceDisable;
    }
    &--approvalrequested {
      background-color: $optionalPink;
    }
    &--recommended {
      background-color: $optionalOrange;
    }
  }

  &__tooltip {
    position: fixed; /* fixedにして、画面全体に対して配置 */
    background-color: black;
    color: white;
    padding: 5px 10px;
    border-radius: 5px;
    white-space: pre;
    z-index: 100000;
    transition: opacity 0.3s;
    opacity: 1;
    text-align: left;

    /* Badgeの中央に揃える */
    transform: translateX(-50%);

    &::after {
      content: '';
      position: absolute;
      top: v-bind(tooltipHeightPixel);
      left: 50%;
      transform: translateX(-50%);
      border-width: 5px;
      border-style: solid;
      border-color: black transparent transparent transparent;
    }
  }
}
</style>

<style lang="scss">
// ツールチップのスタイル
.notification-badge__tooltip {
  // 下に５pxずらす
  white-space: pre-wrap;
}
</style>
